import type { ReactNode } from 'react';

/**
 * Empty-state primitive · per Stage C §3 S2=A.
 * Placeholder box (120×120 `--panel-2`) + heading + body copy + optional CTA.
 *
 * Used by:
 * - Empty Board column («Перетащи тикет сюда» · no CTA · drag affordance only)
 * - Empty Skills Tracker («Пройди первый тикет» + CTA → S01)
 * - Empty Migration backup («Резервная копия удалена · 30 дней прошло»)
 */

export interface EmptyStateProps {
  /** Inline glyph or emoji shown inside the placeholder box. ASCII OK · stays font-safe. */
  glyph?: ReactNode;
  /** Title `<h3>` line · default «Здесь пока пусто». */
  title?: ReactNode;
  /** Body paragraph · `--ink-soft` · context-specific. */
  body?: ReactNode;
  /** Optional primary CTA · renders `.btn--primary`. */
  cta?: { label: string; onClick: () => void };
  /** Compact variant skips the placeholder box · used inside Board columns. */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  glyph,
  title = 'Здесь пока пусто',
  body,
  cta,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={`empty-state${compact ? ' empty-state--compact' : ''}${className ? ` ${className}` : ''}`}
      role="status"
    >
      {!compact && (
        <div className="empty-state__placeholder" aria-hidden="true">
          {glyph ?? '▢'}
        </div>
      )}
      <h3 className="empty-state__title">{title}</h3>
      {body && <p className="empty-state__body">{body}</p>}
      {cta && (
        <button type="button" className="btn btn--primary" onClick={cta.onClick}>
          {cta.label}
        </button>
      )}
    </div>
  );
}
